import css from 'styled-jsx/css';
import { rgba } from 'common/styling/theme';

const staticStyles = css.global`
  @import 'src/common/styling/_mixins-scss/screen-size';
  @import 'src/common/styling/_mixins-scss/rem';

  .BankProofPanel {
    min-width: 0;
    margin-bottom: rem(25px);
    box-shadow: 0 1px 6px 1px rgba(0, 0, 0, 0.06);

    .Collapsible,
    .Collapsible__content {
      margin: 0;
    }

    .Collapsible__header {
      align-items: center;
      gap: rem(20px);
      padding: rem(25px 30px);
      box-shadow: none;
      font-size: rem(26px);
      font-weight: 300;
      text-align: start;

      @include respond-to(sm) {
        padding: rem(20px 15px);
        font-size: rem(20px);
      }
    }
  }

  .BankProofPanel__content {
    padding: rem(25px 30px);

    @include respond-to(sm) {
      padding: rem(20px 15px);
    }
  }

  .BankProofPanel__title {
    margin: 0;
    font-size: rem(20px);
    line-height: 1.4;
  }

  .BankProofPanel__instructions {
    margin: rem(15px 0 25px);
    font-size: rem(13px);
    line-height: 1.5;
  }

  .BankProofPanel__upload {
    padding-bottom: rem(30px);
    margin-bottom: rem(25px);
    border-bottom: 1px solid;
  }

  .BankProofPanel__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: rem(15px);
    margin-bottom: rem(20px);
  }

  .BankProofPanel__filter {
    flex: 0 1 280px;
    min-width: 0;

    .BaseSelectField {
      margin-bottom: 0;
    }

    @include respond-to(xs) {
      flex-basis: 100%;
    }
  }

  .BankProofPanel__list {
    display: flex;
    flex-direction: column;
  }

  .BankProofPanel__document {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: rem(20px);
    padding: rem(20px 0);
    border-top: 1px solid;

    @include respond-to(md) {
      grid-template-columns: minmax(0, 1fr);
      gap: rem(15px);
    }
  }

  .BankProofPanel__document--preview {
    grid-template-columns: 180px minmax(0, 1fr) auto;

    @include respond-to(md) {
      grid-template-columns: 120px minmax(0, 1fr);

      .BankProofPanel__actions {
        grid-column: 2;
      }
    }

    @include respond-to(xs) {
      grid-template-columns: minmax(0, 1fr);

      .BankProofPanel__actions {
        grid-column: 1;
      }
    }
  }

  .BankProofDocument {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 180px;
    min-height: 115px;
    border: 2px solid;
    border-radius: 8px;
    overflow: hidden;

    @include respond-to(md) {
      width: 120px;
      min-height: 80px;
    }

    &--error {
      flex-direction: column;
      gap: rem(10px);
      padding: rem(10px);
      font-size: rem(12px);
      text-align: center;
    }

    .LightBoxImage {
      max-width: 100%;
    }

    .LightBoxImage__inner > img {
      max-width: 100%;
      max-height: 115px;
      object-fit: contain;
    }
  }

  .BankProofDocument__file {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: inherit;
    padding: rem(15px);
    cursor: pointer;

    img {
      max-width: 100%;
      height: 60px;
      object-fit: contain;
    }
  }

  .BasicModal__inner.BankProofDocument__modal {
    width: 80vw;
    max-width: 1000px;
    min-width: 0;

    h2 {
      overflow-wrap: anywhere;
    }
  }

  .BankProofDocument__pdf {
    display: block;
    width: 100%;
    height: 65vh;
    min-height: 240px;
    border: 0;
  }

  .BankProofPanel__details {
    min-width: 0;
  }

  .BankProofPanel__filename {
    display: block;
    margin: rem(0 0 10px);
    font-weight: 500;
    overflow-wrap: anywhere;
  }

  .BankProofPanel__metadata {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: rem(8px 16px);
    font-size: rem(13px);
  }

  .BankProofPanel__status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: rem(11px);
    font-weight: 500;
    letter-spacing: 0.6px;
    text-transform: uppercase;

    &:before {
      content: '';
      width: 7px;
      height: 7px;
      flex-shrink: 0;
      border-radius: 50%;
      background: currentColor;
    }
  }

  .BankProofPanel__reason,
  .BankProofPanel__reviewer {
    margin: rem(10px 0 0);
    overflow-wrap: anywhere;
    line-height: 1.5;
  }

  .BankProofPanel__reason {
    white-space: pre-wrap;
  }

  .BankProofPanel__reviewer {
    font-size: rem(13px);
  }

  .BankProofPanel__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: rem(10px);
  }

  .BankProofPanel__form {
    display: flex;
    flex-direction: column;
    gap: rem(20px);
    max-width: 100%;
  }

  .BankProofPanel__error,
  .BankProofPanel__notice {
    padding: rem(15px);
    margin: rem(0 0 20px);
    overflow-wrap: anywhere;
    line-height: 1.5;
  }

  .BankProofPanel__empty {
    padding: rem(25px 0);
    text-align: center;
  }
`;

const getDynamicStyles = ({ colors }) => {
  return css.global`
    .BankProofPanel {
      background-color: ${colors.white.hex};
    }

    .BankProofPanel__document,
    .BankProofPanel__upload,
    .BankProofDocument {
      border-color: ${rgba([colors.lightGray.rgb, 0.5])};
    }

    .BankProofPanel__instructions,
    .BankProofPanel__metadata,
    .BankProofPanel__reviewer,
    .BankProofPanel__empty,
    .BankProofPanel__status[data-status='archived'] {
      color: ${colors.gray.hex};
    }

    .BankProofPanel__status[data-status='pending'] {
      color: ${colors.warning.hex};
    }

    .BankProofPanel__status[data-status='approved'] {
      color: ${colors.success.hex};
    }

    .BankProofPanel__status[data-status='rejected'],
    .BankProofPanel__status[data-status='revoked'] {
      color: ${colors.error.hex};
    }

    .BankProofPanel__error {
      color: ${colors.error.hex};
      background-color: ${rgba([colors.error.rgb, 0.05])};
    }

    .BankProofDocument--error {
      color: ${colors.error.hex};
    }

    .BankProofPanel__notice {
      background-color: ${rgba([colors.warning.rgb, 0.15])};
    }
  `;
};

export { staticStyles, getDynamicStyles };
